import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import PlanPicker from '@/components/billing/PlanPicker';
import { provisionStarterChecklists } from '@/lib/starterChecklists';


const QuickSetup = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [units, setUnits] = useState<Array<{
    tempId: string;
    name: string;
    address: string;
  }>>([]);

  const totalSteps = 3;

  const steps = [
    { title: "Language & Region", description: "Choose your language and business location" },
    { title: "First Location", description: "Set up your first unit" },
    { title: "Welcome!", description: "You're all set to get started" },
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'da', name: 'Danish', flag: '🇩🇰' },
    { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
  ];

  const regions = [
    { code: 'nordic', name: 'Nordic Countries', description: 'Norway, Sweden, Denmark, Finland, Iceland' },
    { code: 'eu', name: 'European Union', description: 'EU food safety standards and regulations' },
    { code: 'uk', name: 'United Kingdom', description: 'UK Food Standards Agency compliance' },
    { code: 'us', name: 'United States', description: 'FDA and USDA regulations' },
    { code: 'ca', name: 'Canada', description: 'CFIA standards and requirements' },
    { code: 'au', name: 'Australia/New Zealand', description: 'FSANZ food safety standards' },
  ];

  const addUnit = () => {
    setUnits([...units, {
      tempId: `temp-${Date.now()}`,
      name: '',
      address: '',
    }]);
  };

  const removeUnit = (tempId: string) => {
    if (units.length > 1) {
      setUnits(units.filter((u) => u.tempId !== tempId));
    }
  };

  const updateUnit = (tempId: string, field: 'name' | 'address', value: string) => {
    setUnits(units.map((u) => 
      u.tempId === tempId ? { ...u, [field]: value } : u
    ));
  };

  const nextStep = () => {
    if (currentStep === 1 && (!selectedLanguage || !selectedRegion)) {
      toast.error('Please select both language and region to continue');
      return;
    }
    if (currentStep === 2 && units.length === 0) {
      toast.error('Please add at least one location to continue');
      return;
    }
    if (currentStep === 2) {
      const hasEmptyFields = units.some(u => !u.name.trim());
      if (hasEmptyFields) {
        toast.error('Please fill in the location name');
        return;
      }
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeSetup = async () => {
    try {
      // Get user's tenant
      const { data: userRole, error: roleError } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (roleError) throw roleError;
      if (!userRole) throw new Error('User not associated with any organization');

      // Save units
      let firstUnitId: string | null = null;
      for (const unit of units) {
        const { data: createdUnit, error } = await supabase
          .from('units')
          .insert({
            tenant_id: userRole.tenant_id,
            name: unit.name,
            address: unit.address || null,
            timezone: 'UTC'
          })
          .select('id')
          .single();

        if (error) throw error;
        if (!firstUnitId) firstUnitId = createdUnit?.id ?? null;
      }

      // Mark setup as complete in user preferences
      await supabase
        .from('user_preferences')
        .upsert({
          user_id: user?.id,
          setup_completed: true,
        });

      // Give them a working kitchen straight away: standard daily checks,
      // already scheduled for today against their first location.
      let firstChecklistId: string | null = null;
      if (firstUnitId) {
        try {
          firstChecklistId = await provisionStarterChecklists(
            userRole.tenant_id,
            firstUnitId,
            user?.id
          );
        } catch (e) {
          console.error('Could not create starter checklists:', e);
        }
      }

      // Refresh the cached subscription state (it may still hold the old
      // "no business" answer) before leaving setup.
      await queryClient.invalidateQueries({ queryKey: ['subscription-state'] });
      toast.success("You're ready — your daily food safety checks are set up.");
      navigate(firstChecklistId ? `/checklists/${firstChecklistId}/execute` : '/dashboard');
    } catch (error: any) {
      console.error('Error completing setup:', error);
      toast.error(error.message || 'Failed to complete setup');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 p-4">
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Quick Setup</h1>
          <p className="text-muted-foreground">
            Just 3 quick steps to get started • Takes about 2 minutes
          </p>
        </div>

        {/* Progress Bar */}
        <div className="glass-panel p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm text-muted-foreground">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 mb-4">
            <div 
              className="bg-primary h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className={`flex-1 ${index > 0 ? 'ml-2' : ''}`}>
                <div className={`text-xs font-medium ${index + 1 === currentStep ? 'text-primary' : index + 1 < currentStep ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {step.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Language & Region */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Language & Region</CardTitle>
              <CardDescription>
                Select your preferred language and where your business is located
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Language Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Language</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setSelectedLanguage(lang.code);
                        i18n.changeLanguage(lang.code);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedLanguage === lang.code
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xl">{lang.flag}</span>
                        {selectedLanguage === lang.code && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="text-sm font-medium">{lang.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Region Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Business Region</Label>
                <div className="grid grid-cols-1 gap-3">
                  {regions.map((region) => (
                    <button
                      key={region.code}
                      onClick={() => setSelectedRegion(region.code)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedRegion === region.code
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{region.name}</div>
                          <div className="text-sm text-muted-foreground">{region.description}</div>
                        </div>
                        {selectedRegion === region.code && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: First Location */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Add Your First Location</CardTitle>
              <CardDescription>
                Set up your first operational unit (restaurant, kitchen, or facility)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {units.length === 0 ? (
                <Button onClick={addUnit} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              ) : (
                <div className="space-y-4">
                  {units.map((unit, index) => (
                    <div key={unit.tempId} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">Location {index + 1}</h3>
                        {units.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeUnit(unit.tempId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid gap-4">
                        <div>
                          <Label htmlFor={`name-${unit.tempId}`}>
                            Location Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id={`name-${unit.tempId}`}
                            value={unit.name}
                            onChange={(e) => updateUnit(unit.tempId, 'name', e.target.value)}
                            placeholder="e.g., Main Kitchen, Restaurant Downtown"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor={`address-${unit.tempId}`}>Address</Label>
                          <Textarea
                            id={`address-${unit.tempId}`}
                            value={unit.address}
                            onChange={(e) => updateUnit(unit.tempId, 'address', e.target.value)}
                            placeholder="123 Main Street, City, Country"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Choose a plan & finish */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>You're All Set!</CardTitle>
                <CardDescription>
                  Your 7-day free trial is running. Pick a plan now and it starts when the trial ends.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4 text-left bg-muted/50 p-4 rounded-lg">
                  <h3 className="font-semibold text-sm">What's Next?</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Browse and customize templates for your operations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Create checklists and start managing compliance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Invite team members to collaborate</span>
                    </li>
                  </ul>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p className="mb-2">Need help getting started?</p>
                  <p className="flex items-center justify-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email us at <span className="font-medium">info@crystalhaccp.com</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Choose your plan</CardTitle>
                <CardDescription>
                  You can also skip this and decide later — your free trial keeps running either way.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlanPicker defaultUnits={Math.max(1, units.length)} compact />
              </CardContent>
            </Card>
          </div>
        )}


        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          {currentStep < totalSteps && (
            <Button onClick={nextStep}>
              {currentStep === 1 ? 'Continue' : 'Next'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
          
          {currentStep === totalSteps && (
            <Button onClick={completeSetup} size="lg">
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSetup;
